import {
  validateGovernorProductionActionContract,
  type GovernorProductionActionContract,
} from '../action-contract/GovernorProductionActionContract';

import type {
  GovernorRepairExecutionRecord,
} from '../spine/GovernorRepairExecutionLedger';

import type {
  CallsIncidentResponsePlan,
} from './CallsIncidentResponsePlanningEngine';

import type {
  CallsRepairAuthorityResult,
} from './CallsRepairAuthorityGateEngine';

export type CallsExecutionContractStatus =
  | 'ready'
  | 'blocked';

export type CallsExecutionContractReason =
  | 'execution-contract-ready'
  | 'invalid-response-plan'
  | 'invalid-authority-decision'
  | 'plan-authority-mismatch'
  | 'authority-not-approved'
  | 'authority-evidence-missing'
  | 'production-contract-invalid';

export type CallsGovernorExecutionRequest = Readonly<{
  executionId: string;
  idempotencyKey: string;
  governor: 'Calls Governor';
  incidentId: string;
  incidentKey: string;
  planId: string;
  action: string;
  status: CallsExecutionContractStatus;
  reason: CallsExecutionContractReason;
  executionAllowed: boolean;
  authorityEvidenceId: string | null;
  productionActionContract:
    GovernorProductionActionContract;
  ledgerDraft:
    GovernorRepairExecutionRecord;
  rollbackRequiredOnVerificationFailure: true;
  verificationGovernorRequired: true;
  createdAt: string;
}>;

type UnknownRecord = Record<string, unknown>;

const asRecord = (
  value: unknown,
): UnknownRecord =>
  value !== null && typeof value === 'object'
    ? (value as UnknownRecord)
    : {};

const asString = (
  value: unknown,
  fallback: string,
): string =>
  typeof value === 'string' &&
  value.trim().length > 0
    ? value
    : fallback;

const validPlan = (
  value: unknown,
): value is CallsIncidentResponsePlan => {
  const plan = asRecord(value);

  return (
    typeof plan.id === 'string' &&
    plan.id.length > 0 &&
    typeof plan.incidentId === 'string' &&
    plan.incidentId.length > 0 &&
    typeof plan.incidentKey === 'string' &&
    plan.incidentKey.length > 0 &&
    typeof plan.recommendedAction === 'string' &&
    plan.verificationRequired === true
  );
};

const validAuthority = (
  value: unknown,
): value is CallsRepairAuthorityResult => {
  const authority = asRecord(value);

  return (
    typeof authority.planId === 'string' &&
    typeof authority.incidentId === 'string' &&
    typeof authority.incidentKey === 'string' &&
    typeof authority.decision === 'string' &&
    typeof authority.executionAllowed === 'boolean'
  );
};

const blockedContract = (
  reason: CallsExecutionContractReason,
  createdAt: string,
): CallsGovernorExecutionRequest =>
  Object.freeze({
    executionId: 'calls-execution:blocked',
    idempotencyKey: 'calls-execution:blocked',
    governor: 'Calls Governor',
    incidentId: 'Unknown',
    incidentKey: 'Unknown',
    planId: 'Unknown',
    action: 'none',
    status: 'blocked',
    reason,
    executionAllowed: false,
    authorityEvidenceId: null,
    productionActionContract: Object.freeze({
      rootCause: 'Unknown',
      owningFiles: [],
      evidence: [],
      safePatchPlan: [],
      rollbackPlan: [],
      verificationCommands: [],
    }),
    ledgerDraft: Object.freeze({
      findingId: 'Unknown',
      repairRoute: 'Blocked',
      executedBy: 'Calls Governor',
      status: 'PENDING',
      executedAt: createdAt,
    }),
    rollbackRequiredOnVerificationFailure: true,
    verificationGovernorRequired: true,
    createdAt,
  });

export function buildCallsGovernorExecutionRequest(
  planValue: unknown,
  authorityValue: unknown,
  createdAtValue: unknown,
): CallsGovernorExecutionRequest {
  const createdAt = asString(
    createdAtValue,
    'Unknown',
  );

  if (!validPlan(planValue)) {
    return blockedContract(
      'invalid-response-plan',
      createdAt,
    );
  }

  if (!validAuthority(authorityValue)) {
    return blockedContract(
      'invalid-authority-decision',
      createdAt,
    );
  }

  const plan = planValue;
  const authority = authorityValue;

  if (
    authority.planId !== plan.id ||
    authority.incidentId !== plan.incidentId ||
    authority.incidentKey !== plan.incidentKey
  ) {
    return blockedContract(
      'plan-authority-mismatch',
      createdAt,
    );
  }

  if (
    authority.decision !== 'approved' ||
    authority.executionAllowed !== true
  ) {
    return blockedContract(
      'authority-not-approved',
      createdAt,
    );
  }

  if (
    typeof authority.authorityEvidenceId !==
      'string' ||
    authority.authorityEvidenceId.length === 0
  ) {
    return blockedContract(
      'authority-evidence-missing',
      createdAt,
    );
  }

  const executionId =
    `calls-execution:${plan.incidentKey}`;

  const idempotencyKey = [
    'calls',
    plan.incidentKey,
    plan.recommendedAction,
    authority.authorityEvidenceId,
  ].join(':');

  const productionActionContract:
    GovernorProductionActionContract =
      Object.freeze({
        rootCause: plan.reasonCode,
        owningFiles: Object.freeze([
          'src/governor/calls',
          'src/governor/calls-runtime',
        ]) as unknown as string[],
        evidence: Object.freeze([
          authority.authorityEvidenceId,
          plan.incidentId,
          plan.id,
        ]) as unknown as string[],
        safePatchPlan: Object.freeze([
          plan.recommendedAction,
        ]) as unknown as string[],
        rollbackPlan: Object.freeze([
          'Restore pre-execution Calls runtime state',
          'Record rollback evidence',
          'Return incident to active verification',
        ]) as unknown as string[],
        verificationCommands: Object.freeze([
          'Verify Calls runtime evidence',
          'Verify incident state',
          'Verify call lifecycle behavior',
        ]) as unknown as string[],
      });

  const validation =
    validateGovernorProductionActionContract(
      productionActionContract,
    );

  if (validation.status !== 'PASS') {
    return blockedContract(
      'production-contract-invalid',
      createdAt,
    );
  }

  const ledgerDraft:
    GovernorRepairExecutionRecord =
      Object.freeze({
        findingId: plan.incidentId,
        repairRoute: plan.recommendedAction,
        executedBy: 'Calls Governor',
        status: 'PENDING',
        executedAt: createdAt,
      });

  return Object.freeze({
    executionId,
    idempotencyKey,
    governor: 'Calls Governor',
    incidentId: plan.incidentId,
    incidentKey: plan.incidentKey,
    planId: plan.id,
    action: plan.recommendedAction,
    status: 'ready',
    reason: 'execution-contract-ready',
    executionAllowed: true,
    authorityEvidenceId:
      authority.authorityEvidenceId,
    productionActionContract,
    ledgerDraft,
    rollbackRequiredOnVerificationFailure: true,
    verificationGovernorRequired: true,
    createdAt,
  });
}
